import { useEffect, useState } from 'react';
import { CheckoutOffer, resolveCheckoutOffer } from './offer';
import { fetchPriceBreakdown, PriceBreakdown } from './pricing';

export interface CheckoutOfferState {
  /** True once resolution has finished, whether or not it found an offer. */
  resolved: boolean;
  offer: CheckoutOffer | null;
  breakdown: PriceBreakdown | null;
}

/**
 * Resolves the offer and its pricing for the in-site checkout screens.
 *
 * Shared by the bag and the checkout so both quote the same figures from the same fetch, and so the
 * fallback condition (`resolved` with no `breakdown`) is defined in one place rather than judged twice.
 *
 * @param gwaShopUrl the chip's GWA shop URL, or null for a chip with no rescue offer
 * @param ready false while the caller is still loading the chip; resolution waits for it
 */
export function useCheckoutOffer(gwaShopUrl: string | null, ready: boolean): CheckoutOfferState {
  const [state, setState] = useState<CheckoutOfferState>({ resolved: false, offer: null, breakdown: null });

  useEffect(() => {
    if (!ready) {
      return;
    }

    let cancelled = false;
    void (async () => {
      const offer = await resolveCheckoutOffer(gwaShopUrl);
      const breakdown = offer ? await fetchPriceBreakdown(offer.planSku) : null;
      if (!cancelled) {
        setState({ resolved: true, offer, breakdown });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, gwaShopUrl]);

  return state;
}
